import React, { useRef, useState } from "react";
import "./ImageZoomPan.css";

const ImageZoomPan = ({ src }) => {
  const imgRef = useRef(null);
  const containerRef = useRef(null);

  const [zoomed, setZoomed] = useState(false);
  const [scale, setScale] = useState(1);
  const [origin, setOrigin] = useState({ x: 50, y: 50 });
  const [translate, setTranslate] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [startDrag, setStartDrag] = useState({ x: 0, y: 0 });
  const [dragged, setDragged] = useState(false);

  const resetZoom = () => {
    setZoomed(false);
    setScale(1);
    setTranslate({ x: 0, y: 0 });
  };

  const zoomIn = () => {
    setZoomed(true);
    setScale((prev) => prev + 0.3);
  };

  const zoomOut = () => {
    setScale((prev) => {
      const next = prev - 0.3;
      if (next <= 1) {
        resetZoom();
        return 1;
      }
      return next;
    });
  };

  const toggleZoom = (e) => {
    if (dragged) {
      setDragged(false);
      return;
    }

    const rect = imgRef.current.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * 100;
    const py = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin({ x: px, y: py });

    if (!zoomed) {
      zoomIn();
    } else {
      resetZoom();
    }
  };

  const onWheel = (e) => {
    e.preventDefault();
    const img = imgRef.current;
    if (!img) return;

    const rect = img.getBoundingClientRect();
    const px = ((e.clientX - rect.left) / rect.width) * 100;
    const py = ((e.clientY - rect.top) / rect.height) * 100;
    setOrigin({ x: px, y: py });

    if (e.deltaY < 0) zoomIn();
    else zoomOut();
  };

  const onMouseDown = (e) => {
    if (!zoomed) return;
    setDragging(true);
    setStartDrag({ x: e.clientX - translate.x, y: e.clientY - translate.y });
  };

  const onMouseMove = (e) => {
    if (!dragging || !zoomed) return;

    const container = containerRef.current;
    const img = imgRef.current;
    if (!container || !img) return;

    const maxX = (img.offsetWidth * scale - container.offsetWidth) / 2;
    const maxY = (img.offsetHeight * scale - container.offsetHeight) / 2;

    let x = e.clientX - startDrag.x;
    let y = e.clientY - startDrag.y;

    x = Math.max(-maxX, Math.min(maxX, x));
    y = Math.max(-maxY, Math.min(maxY, y));

    setTranslate({ x, y });
    setDragged(true);
  };

  const onMouseUp = () => setDragging(false);
  const onMouseLeave = () => setDragging(false);

  return (
    <div className="zoom-wrapper">
      <div
        ref={containerRef}
        className={`zoom-container ${zoomed ? "zoomed" : ""}`}
        onMouseDown={onMouseDown}
        onMouseMove={onMouseMove}
        onMouseUp={onMouseUp}
        onMouseLeave={onMouseLeave}
        onClick={toggleZoom}
        onWheel={onWheel}
        style={{ cursor: zoomed ? (dragging ? "grabbing" : "grab") : "zoom-in" }}
      >
        <img
          ref={imgRef}
          src={src}
          alt="zoom"
          style={{
            transformOrigin: `${origin.x}% ${origin.y}%`,
            transform: `scale(${scale}) translate(${translate.x}px, ${translate.y}px)`,
            userSelect: "none",
            pointerEvents: "none",
            transition: dragging ? "none" : "transform 0.25s ease",
            maxWidth: "100%",
            maxHeight: "80vh",
          }}
        />
      </div>

      <div className="zoom-controls">
        <button onClick={zoomIn}>+</button>
        <button onClick={zoomOut}>−</button>
      </div>
    </div>
  );
};

export default ImageZoomPan;
